import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/lib/supabase';

export default function ChangeEmailScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();

  const [fullName, setFullName] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [alertModalTitle, setAlertModalTitle] = useState('');
  const [alertModalMessage, setAlertModalMessage] = useState('');
  const [alertModalOnOk, setAlertModalOnOk] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (user?.email) setCurrentEmail(user.email);
  }, [user?.email]);

  useEffect(() => {
    const name =
      (user?.user_metadata?.full_name as string | undefined) ??
      (user?.user_metadata?.display_name as string | undefined) ??
      '';
    setFullName(name);
  }, [user?.user_metadata?.full_name, user?.user_metadata?.display_name]);

  const showAlert = (title: string, message: string, onOk?: () => void) => {
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setAlertModalOnOk(onOk ?? (() => setAlertModalVisible(false)));
    setAlertModalVisible(true);
  };

  const handleAlertOk = () => {
    const onOk = alertModalOnOk;
    setAlertModalVisible(false);
    setAlertModalOnOk(null);
    onOk?.();
  };

  const updateProfile = async () => {
    const emailChanging = newEmail.trim() && newEmail.trim() !== currentEmail;
    const nameChanging = fullName.trim() !== (user?.user_metadata?.full_name ?? user?.user_metadata?.display_name ?? '');

    if (!nameChanging && !emailChanging) {
      showAlert('Error', 'No changes to save.');
      return;
    }

    if (emailChanging) {
      if (!password) {
        showAlert('Error', 'Please enter your password to change your email.');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail.trim())) {
        showAlert('Error', 'Please enter a valid email address.');
        return;
      }
    }

    setLoading(true);

    try {
      if (nameChanging) {
        const { error } = await supabase.auth.updateUser({
          data: { full_name: fullName.trim() },
        });
        if (error) {
          showAlert('Error', 'Unable to update your name. Please try again.');
          setLoading(false);
          return;
        }
      }

      if (emailChanging) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: currentEmail,
          password,
        });

        if (signInError) {
          showAlert('Error', 'Password is incorrect.');
          setLoading(false);
          return;
        }

        const { error } = await supabase.auth.updateUser({
          email: newEmail.trim(),
        });

        if (error) {
          let errorMessage = 'Unable to update your email address. Please try again.';
          if (error.message.includes('already registered') || error.message.includes('already exists')) {
            errorMessage = 'This email address is already in use by another account.';
          } else if (error.message.includes('Invalid email')) {
            errorMessage = 'Please enter a valid email address.';
          }
          showAlert('Email update failed', errorMessage);
          setLoading(false);
          return;
        }

        showAlert(
          'Verification required',
          'Check both your old and new email addresses. You must confirm the change in both inboxes.',
          () => {
            setNewEmail('');
            setPassword('');
            router.back();
          }
        );
      } else {
        showAlert('Success', 'Your profile has been updated.', () => router.back());
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      showAlert('Error', 'An unexpected error occurred. Please try again.');
    }

    setLoading(false);
  };

  const emailChanging = !!newEmail.trim() && newEmail.trim() !== currentEmail;
  const nameChanging = fullName.trim() !== (user?.user_metadata?.full_name ?? user?.user_metadata?.display_name ?? '');
  const isFormValid =
    nameChanging ||
    (emailChanging &&
      !!password &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim()));

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: Math.max(insets.top, 16) },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
            accessibilityLabel="Go back"
          >
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>

          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <Ionicons name="person" size={48} color={Brand.primary} />
            </View>
            <Text style={styles.title}>Edit profile</Text>
            <Text style={styles.subtitle}>
              Update your name and email. Email changes require verification in both inboxes.
            </Text>
          </View>

          <View style={styles.card}>
            <Text style={styles.label}>Name</Text>
            <Input
              value={fullName}
              onChangeText={setFullName}
              placeholder="Your name"
              autoCapitalize="words"
              style={styles.input}
              editable={!loading}
            />

            <Text style={[styles.label, styles.labelSpaced]}>Current email</Text>
            <Text style={styles.currentEmail}>{currentEmail || 'Loading...'}</Text>

            <Text style={[styles.label, styles.labelSpaced]}>New email</Text>
            <Input
              value={newEmail}
              onChangeText={setNewEmail}
              placeholder="you@example.com"
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              style={styles.input}
              editable={!loading}
            />

            <Text style={[styles.label, styles.labelSpaced]}>Password</Text>
            <Input
              value={password}
              onChangeText={setPassword}
              placeholder="Required only when changing email"
              secureTextEntry
              autoCapitalize="none"
              style={styles.input}
              editable={!loading}
            />

            <Button
              onPress={updateProfile}
              disabled={!isFormValid || loading}
              style={styles.submitButton}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                'Update profile'
              )}
            </Button>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      <Modal
        visible={alertModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleAlertOk}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{alertModalTitle}</Text>
            <Text style={styles.modalDescription}>{alertModalMessage}</Text>
            <View style={styles.modalActions}>
              <Button onPress={handleAlertOk} style={styles.modalButton}>
                OK
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 24,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  header: {
    marginBottom: 32,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: primaryAlpha(0.2),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    lineHeight: 24,
  },
  card: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.06)',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  labelSpaced: {
    marginTop: 16,
  },
  currentEmail: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 4,
  },
  input: {
    marginBottom: 0,
  },
  submitButton: {
    marginTop: 24,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: Neutrals.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
});
